import { FC } from 'react';
import homeMessages from './messages';
import pagesMessages from '../messages';
import { useIntl } from 'react-intl';
import { Stack, Link, DefaultButton, IButtonStyles } from '@fluentui/react';
import { useStateRouterStore } from '../../app/stateRouter/useStateRouterStore';
import { FeatureFlags, OrganizationSelect } from '../../features/organizations';
import { useOrganizationsStore } from '../../features/organizations/store';
import { APP_BASE_URL } from '../../config';

const buttonStyles: Partial<IButtonStyles> = {
  textContainer: {
    flexGrow: 0,
  },
};

export const Home: FC = () => {
  const { setSelectedOrganizationOption } = useOrganizationsStore();
  const intl = useIntl();
  const { setCurrentRoute } = useStateRouterStore();

  const organizationSettingsMeta = useOrganizationsStore(
    state => state.organizationSettingsMeta,
  );
  const currentOrgSettings = useOrganizationsStore(
    state => state.currentOrganizationSettings,
  );

  const selectedOrgOptions = useOrganizationsStore(
    state => state.selectedOrganizationOption,
  );

  const organizations = useOrganizationsStore(state => state.organizations);

  const canUploadToExistingProject =
    currentOrgSettings?.[FeatureFlags.CAN_UPLOAD_DOCUMENTS];

  const canUploadToArchive =
    currentOrgSettings?.[FeatureFlags.CAN_UPLOAD_TO_ARCHIVE];

  const showActionButtons = organizationSettingsMeta.success;

  const noActiveOrganization =
    organizationSettingsMeta.success &&
    organizations.length > 0 &&
    organizations.every(org => !(org.subscriptionStatus === 'active'));

  if (noActiveOrganization) {
    return (
      <Stack
        tokens={{ childrenGap: 20 }}
        style={{ marginTop: 20 }}
        verticalFill>
        <Stack.Item>
          <p>{intl.formatMessage(homeMessages.noActiveOrganizations)}</p>
          <Link
            target="_blank"
            href={`${APP_BASE_URL}/organization/integrations`}>
            {intl.formatMessage(homeMessages.noActiveOrganizationsLink)}
          </Link>
        </Stack.Item>
      </Stack>
    );
  }

  return (
    <>
      <p>
        {intl.formatMessage(homeMessages.homeDescription)}{' '}
        <Link
          target="_blank"
          href="https://help.precisely.se/en/precisely-microsoft-outlook-e-mail-integration">
          {intl.formatMessage(pagesMessages.learnMore)}
        </Link>
      </p>
      <Stack
        tokens={{
          childrenGap: 16,
        }}>
        <Stack.Item>
          <OrganizationSelect
            onChange={(_, organization) => {
              setSelectedOrganizationOption(organization.key);
            }}
            selected={selectedOrgOptions}
          />
        </Stack.Item>
        {showActionButtons && (
          <>
            <DefaultButton
              data-testid="home.create-new-project.button"
              iconProps={{ iconName: 'PageAdd' }}
              styles={buttonStyles}
              onClick={() => {
                setCurrentRoute('/projects/new');
              }}>
              {intl.formatMessage(homeMessages.buttonCreateNewProject)}
            </DefaultButton>
            {canUploadToExistingProject && (
              <DefaultButton
                data-testid="home.update-project.button"
                iconProps={{ iconName: 'PageCheckedin' }}
                styles={buttonStyles}
                onClick={() => {
                  setCurrentRoute('/projects/update');
                }}>
                {intl.formatMessage(homeMessages.buttonUploadToExistingProject)}
              </DefaultButton>
            )}
            {canUploadToArchive && (
              <DefaultButton
                data-testid="home.upload-to-archive.button"
                iconProps={{ iconName: 'Upload' }}
                styles={buttonStyles}
                onClick={() => {
                  setCurrentRoute('/archive/upload');
                }}>
                {intl.formatMessage(homeMessages.buttonUploadToArchive)}
              </DefaultButton>
            )}
          </>
        )}
      </Stack>
    </>
  );
};
